import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/profileService';
import AccountDetails from './components/AccountDetails';
import SecuritySettings from './components/SecuritySettings';
import WorkspacePreferences from './components/WorkspacePreferences';
import ActivityLog from './components/ActivityLog';
import { Helmet } from 'react-helmet';

export default function UserProfile() {
  const { user, profile: authProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('account');

  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load profile
      const profileData = await profileService?.getProfile(user?.id);
      setProfile(profileData);

      // Load activity logs
      const logs = await profileService?.getActivityLogs(user?.id, 20);
      setActivityLogs(logs);
    } catch (err) {
      setError(err?.message || 'Failed to load profile data');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (updates) => {
    try {
      const updatedProfile = await profileService?.updateProfile(user?.id, updates);
      setProfile(updatedProfile);
      
      // Log the update
      await profileService?.logActivity(
        user?.id,
        'profile_update',
        'Profile information updated',
        null,
        navigator.userAgent
      );
      
      // Reload activity logs
      const logs = await profileService?.getActivityLogs(user?.id, 20);
      setActivityLogs(logs);
      
      return updatedProfile;
    } catch (err) {
      throw err;
    }
  };

  const tabs = [
    { id: 'account', label: 'Account Details', icon: '👤' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'workspace', label: 'Workspace', icon: '🏢' },
    { id: 'activity', label: 'Activity Log', icon: '📊' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-600 text-center mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Error Loading Profile</h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={loadProfileData}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>User Profile - Settings & Preferences</title>
        <meta name="description" content="Manage your account details, security settings, and workspace preferences" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {profile?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{profile?.fullName}</h1>
                  <p className="text-gray-500">{profile?.email}</p>
                  <span className="inline-block mt-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {profile?.role}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Member since</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(profile?.createdAt)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab?.id
                        ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <span className="mr-2">{tab?.icon}</span>
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {activeTab === 'account' && (
              <AccountDetails 
                profile={profile}
                onUpdate={handleProfileUpdate}
              />
            )}
            
            {activeTab === 'security' && (
              <SecuritySettings 
                profile={profile}
                user={user}
                onUpdate={handleProfileUpdate}
              />
            )}
            
            {activeTab === 'workspace' && (
              <WorkspacePreferences 
                profile={profile}
                onUpdate={handleProfileUpdate}
              />
            )}
            
            {activeTab === 'activity' && (
              <ActivityLog 
                logs={activityLogs}
                onRefresh={loadProfileData}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}